import { useAuthContext } from './use-auth-context.hook';
import { ApiMethod } from '../models/api-types.type';

/**
 * The insight engine's action queue.
 *
 * Findings are produced by deterministic detectors on the server and stored;
 * this reads them. Nothing here recomputes anything, and the page deliberately
 * never triggers a sweep — see `queue` on the controller for why.
 */

export type InsightSeverity = 'critical' | 'warning' | 'info';
export type InsightKind =
    | 'movement'
    | 'rank_shift'
    | 'funnel_drop'
    | 'blind_metric'
    /** A product earns attention but has no content made for it — the one kind whose action is work. */
    | 'content_gap'
    /** Something the loop needs is not set up: website, socials, brand, first posts. */
    | 'setup_gap'
    /** Proposed by the business analyst, from what it read of the site, catalogue and search. */
    | 'analysis'
    /** Search visibility moved since last month, or a named competitor is far ahead. */
    | 'seo_shift'
    /** Products drifting away from how the vendor organised their catalogue. */
    | 'catalogue_gap';
export type InsightStatus = 'open' | 'acknowledged' | 'resolved';
/** Whether the movement is welcome — separate from severity, which is urgency. */
export type Sentiment = 'good' | 'bad' | 'neutral';

export interface Insight {
    findingId: string;
    kind: InsightKind;
    severity: InsightSeverity;
    sentiment: Sentiment;
    metric: string;
    dimension?: string;
    dimensionKey?: string;
    title: string;
    detail: string;
    /** The movement in things rather than percentages. */
    consequence?: string;
    /**
     * The same three sentences as translation keys.
     *
     * Absent on findings written before this existed and on the analyst's,
     * which are already prose in the vendor's language — so the strings above
     * remain the fallback rather than being replaced.
     */
    i18n?: { title?: string; detail?: string; consequence?: string; params?: Record<string, string | number> };
    value: number | null;
    previous?: number;
    change?: number | null;
    direction: 'up' | 'down' | 'flat';
    periodFrom: string;
    periodTo: string;
    score: number;
    status: InsightStatus;
    suggestedAction?: { kind: string; params?: Record<string, unknown> };
    lastSeenAt: string;
}

export interface InsightQueue {
    items: Insight[];
    counts: { critical: number; warning: number; info: number };
    /**
     * One sentence over the whole queue, composed server-side from the very
     * rows returned — so it cannot disagree with the cards underneath it.
     */
    verdict: {
        tone: Sentiment;
        text: string;
        /** The same sentence as keys. `lead` is the worst finding's own. */
        i18n?: {
            key: string;
            params?: Record<string, string | number>;
            lead?: { title?: string; detail?: string; consequence?: string; params?: Record<string, string | number> };
            leadText?: string;
        };
    } | null;
    /** What the engine looked for — so an empty queue can explain itself. */
    detectors: { kind: string; description: string }[];
}

const BASE = '/services/pms/insights';

export const useInsightsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const getQueue = async (opts: { limit?: number; status?: InsightStatus } = {}): Promise<InsightQueue> => {
        const params = new URLSearchParams();
        if (opts.limit) params.set('limit', String(opts.limit));
        if (opts.status) params.set('status', opts.status);

        const query = params.toString();
        const response: any = await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/queue${query ? `?${query}` : ''}`);
        return response?.data ?? response;
    };

    /** Park a finding. The engine resolves things that stop being true on its own. */
    const acknowledge = async (findingId: string): Promise<void> => {
        await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/acknowledge`, { findingId });
    };

    /**
     * Run the detectors over a window and store what they find.
     *
     * Exposed for a manual refresh; the queue is normally filled by the
     * scheduled sweep, because forty aggregations is not a page load.
     */
    const sweep = async (startDate: string, endDate: string): Promise<{ findings: Insight[] }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            `${BASE}/sweep?startDate=${startDate}&endDate=${endDate}`,
        );
        return response?.data ?? response;
    };

    return { getQueue, acknowledge, sweep };
};
